import { useConnectModal, useAccountModal } from '@rainbow-me/rainbowkit';
import React from 'react';
import { useDisconnect, useAccountEffect, useConfig } from 'wagmi';

import * as mixpanel from 'lib/mixpanel/index';
import useAccount from 'lib/web3/useAccount';

interface Params {
  source: mixpanel.EventPayload<mixpanel.EventTypes.WALLET_CONNECT>['Source'];
}

export default function useWeb3Wallet({ source }: Params) {
  const { openConnectModal, connectModalOpen } = useConnectModal();
  const { openAccountModal } = useAccountModal();
  const { disconnect } = useDisconnect();
  const config = useConfig();
  const [ isOpening, setIsOpening ] = React.useState(false);
  const [ isClientLoaded, setIsClientLoaded ] = React.useState(false);
  const isConnectionStarted = React.useRef(false);

  React.useEffect(() => {
    setIsClientLoaded(true);
  }, []);

  const account = useAccount();

  // When switching between two connected wallets, wagmi briefly surfaces a phantom
  // wallet's address (a previously authorized wallet such as OKX reconnects alongside the
  // one the user picked) before the single-wallet enforcer settles it. We debounce ONLY
  // the address, and ONLY for that case — first connect, disconnect and page-navigation
  // remounts apply immediately so the UI never flashes "disconnected".
  const rawAddress = account.address;
  const [ stableAddress, setStableAddress ] = React.useState(rawAddress);
  const [ isSettling, setIsSettling ] = React.useState(false);

  React.useEffect(() => {
    if (rawAddress === stableAddress) {
      return;
    }
    // Connecting from scratch or disconnecting: reflect it immediately.
    if (!rawAddress || !stableAddress) {
      setStableAddress(rawAddress);
      setIsSettling(false);
      return;
    }
    // Address changed from one wallet to another: wait for it to stay stable.
    setIsSettling(true);
    const timer = setTimeout(() => {
      setStableAddress(rawAddress);
      setIsSettling(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [ rawAddress, stableAddress ]);

  const address = stableAddress;

  const openModal = React.useCallback(() => {
    if (account.address) {
      openAccountModal?.();
    } else {
      openConnectModal?.();
    }
  }, [ account.address, openAccountModal, openConnectModal ]);

  const handleConnect = React.useCallback(async() => {
    setIsOpening(true);
    openConnectModal?.();
    setIsOpening(false);
    mixpanel.logEvent(mixpanel.EventTypes.WALLET_CONNECT, { Source: source, Status: 'Started' });
    isConnectionStarted.current = true;
  }, [ openConnectModal, source ]);

  const handleAccountConnected = React.useCallback(({ isReconnected }: { isReconnected: boolean }) => {
    if (!isReconnected && isConnectionStarted.current) {
      mixpanel.logEvent(mixpanel.EventTypes.WALLET_CONNECT, { Source: source, Status: 'Connected' });
      mixpanel.userProfile.setOnce({
        'With Connected Wallet': true,
      });
    }
    isConnectionStarted.current = false;
  }, [ source ]);

  const handleDisconnect = React.useCallback(() => {
    // wagmi keeps a separate connection per connector and `disconnect()` without
    // arguments only drops the active one. The single-wallet enforcer leaves the
    // previous wallet's connection in the background, so on an explicit disconnect we
    // tear down every active connection. Read them on demand from config (instead of a
    // reactive useConnections) so this hook doesn't re-render every wallet button on
    // each connection change.
    const connections = Array.from(config.state.connections.values());
    if (connections.length === 0) {
      disconnect();
      return;
    }
    connections.forEach((connection) => {
      disconnect({ connector: connection.connector });
    });
  }, [ config, disconnect ]);

  useAccountEffect({ onConnect: handleAccountConnected });

  const isConnected = isClientLoaded && !account.isDisconnected && account.address !== undefined;

  return React.useMemo(() => ({
    connect: handleConnect,
    disconnect: handleDisconnect,
    isOpen: isOpening || connectModalOpen || isSettling,
    isConnected,
    isReconnecting: account.isReconnecting,
    address,
    openModal,
  }), [ handleConnect, handleDisconnect, isOpening, connectModalOpen, isSettling, isConnected, account.isReconnecting, address, openModal ]);
}
